import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";

const ProductCard = ({ product, eager = false, slugPrefix = "catalogue", compact = false }) => {
  const images =
    product.images && product.images.length
      ? product.images
      : [product.image, product.hoverImage].filter(Boolean);
  const [idx, setIdx] = useState(0);
  const total = images.length;
  const hasGallery = total > 1;

  const go = (delta) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx((idx + delta + total) % total);
  };

  const padImg = compact ? "p-3" : "p-6";
  const insetCls = compact
    ? "inset-3 max-w-[calc(100%-1.5rem)] max-h-[calc(100%-1.5rem)]"
    : "inset-6 max-w-[calc(100%-3rem)] max-h-[calc(100%-3rem)]";
  const bodyCls = compact ? "p-4" : "p-6 md:p-7";
  const titleCls = compact ? "font-serif text-lg text-ink-900 leading-tight" : "font-serif text-2xl text-ink-900 leading-tight";
  const descCls = compact
    ? "mt-2 text-stone-700 leading-relaxed text-xs flex-1"
    : "mt-3 text-stone-700 leading-relaxed text-sm flex-1";
  const tagCls = compact
    ? "font-mono text-[9px] tracking-[0.2em] uppercase text-terracotta-600 mb-2"
    : "font-mono text-[10px] tracking-[0.25em] uppercase text-terracotta-600 mb-3";
  const linkCls = compact
    ? "mt-3 inline-flex items-center gap-2 text-ink-900 text-xs font-medium border-b border-ink-900 pb-1 self-start hover:gap-4 transition-all"
    : "mt-5 inline-flex items-center gap-2 text-ink-900 text-sm font-medium border-b border-ink-900 pb-1 self-start hover:gap-4 transition-all";

  return (
    <article
      data-testid={`${slugPrefix}-product-${product.slug}`}
      className="group bg-white border border-stone-200 rounded-sm overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:shadow-ink-900/5 transition-all duration-500 flex flex-col"
    >
      <div className={`aspect-square overflow-hidden bg-white ${padImg} relative`}>
        {images.map((src, i) => (
          <img
            key={i}
            src={src}
            alt={product.name}
            loading={eager && i === 0 ? "eager" : "lazy"}
            className={`absolute ${insetCls} m-auto object-contain transition-opacity duration-500 ease-out ${
              i === idx ? "opacity-100 group-hover:scale-105" : "opacity-0"
            } transition-transform`}
          />
        ))}
        {hasGallery && (
          <>
            <button
              type="button"
              onClick={go(-1)}
              data-testid={`gallery-prev-${product.slug}`}
              aria-label="Previous image"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow-md grid place-items-center text-ink-900 opacity-0 group-hover:opacity-100 hover:bg-white transition-opacity duration-300 z-10"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={go(1)}
              data-testid={`gallery-next-${product.slug}`}
              aria-label="Next image"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow-md grid place-items-center text-ink-900 opacity-0 group-hover:opacity-100 hover:bg-white transition-opacity duration-300 z-10"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
              {images.map((_, i) => (
                <span
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    i === idx ? "bg-ink-900" : "bg-stone-300"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
      <div className={`${bodyCls} flex-1 flex flex-col`}>
        <span className={tagCls}>
          {product.category}
        </span>
        <h3 className={titleCls}>
          {product.name}
        </h3>
        <p className={descCls}>
          {product.description}
        </p>
        <Link
          to={`/?inquiry=${encodeURIComponent(product.name)}#contact`}
          data-testid={`${slugPrefix}-inquire-${product.slug}`}
          className={linkCls}
        >
          Inquire <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </article>
  );
};

export default ProductCard;
